import { fetchGQL } from '@alfa-bank/site-invest-common/http/graph-gql';
import { INVEST_EMITENTS_GRAPHQL_API } from '@alfa-bank/site-invest-env-config';
import { updateAssetData } from '@alfa-bank/site-invest-emitents-common/helpers';
import { defaultConfigUrlToAssetTemplatePage } from '@alfa-bank/site-invest-emitents-common/config';
import { ERROR_REQUEST_FOR_EMITENTS } from '@alfa-bank/site-invest-emitents-common/constants';
import type {
    TAssetTypeCode,
    IGetAssetsResponse,
    TConfigUrlToAssetTemplatePage,
    IAsset,
} from '@alfa-bank/site-invest-emitents-common/src/models';

import { createQueryForSearchField } from './query';

interface IUnit<T> {
    assets: T[];
    totalCount: number;
}

export type TFindAssets<T> = Partial<Record<TAssetTypeCode, IUnit<T>>>;

export interface IFindResult<T> {
    assets: TFindAssets<T>;
    count: number;
}

export interface IFindAssetArgs {
    configUrlToAssetTemplatePage?: TConfigUrlToAssetTemplatePage;
    controller: AbortController;
    limit: number;
    value: string;
}

type TFindAssetsRequestResponse<T> = Record<TAssetTypeCode, IGetAssetsResponse<T>>;

export const findAssets = async <T extends IAsset>({
    configUrlToAssetTemplatePage = defaultConfigUrlToAssetTemplatePage,
    controller,
    value,
    limit,
}: IFindAssetArgs): Promise<IFindResult<T> | undefined> => {
    try {
        const query = createQueryForSearchField(value, limit);
        const { data } = await fetchGQL<TFindAssetsRequestResponse<T>>({
            requestParams: { query },
            fetchErrorText: ERROR_REQUEST_FOR_EMITENTS,
            endpointUrl: INVEST_EMITENTS_GRAPHQL_API(),
            config: { signal: controller.signal },
        });

        let count = 0;

        const assetsData = Object.entries<IGetAssetsResponse<T>>(data);
        const result = assetsData.reduce((acc, [assetType, assetData]) => {
            const { assets, pagination } = assetData;
            const { totalCount } = pagination;

            count += totalCount;

            if (totalCount > 0) {
                return {
                    ...acc,
                    [assetType]: {
                        assets: assets.map(updateAssetData<T>(configUrlToAssetTemplatePage)),
                        totalCount,
                    },
                };
            }

            return acc;
        }, {});

        return { assets: result, count };
    } catch (error) {
        console.error(error);
    }
};
